import { css } from 'lit';

export const SHARED_ELEMENTS = css`
  h1 {
    font-size: var(--fs-display);
    line-height: var(--lh-display);
    letter-spacing: var(--ls-display);
    font-weight: var(--fw-regular);
    margin: 0;
  }

  h2 {
    font-size: var(--fs-headline);
    line-height: var(--lh-headline);
    letter-spacing: var(--ls-headline);
    font-weight: var(--fw-regular);
    margin: 0;
  }

  h3 {
    font-size: var(--fs-title);
    line-height: var(--lh-title);
    letter-spacing: var(--ls-title);
    font-weight: var(--fw-regular);
    margin: 0;
  }

  p {
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    letter-spacing: var(--ls-body);
    font-weight: var(--fw-regular);
    margin: 0;
  }

  a,
  a:visited,
  a:hover,
  a:active {
    color: inherit;
    text-decoration: none;
  }

  /* Inline icons carry their intrinsic size as width/height attributes.
     Letting both axes go auto means a rule setting one dimension scales the
     other proportionally, the way the <img> tags these replaced did. */
  svg {
    width: auto;
    height: auto;
  }

  /* Rich-text list defaults for anything Trix (or any future rich editor)
     produces via unsafeHTML. Kept minimal so container font settings win —
     each block styles its own rich-text container to taste. */
  ul,
  ol {
    margin: 0;
    padding-left: 1.2em;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
  }

  li {
    margin: 0;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
  }

  * {
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgb(34, 34, 34) white;
  }

  *::-webkit-scrollbar-track {
    background-color: #ffffff;
  }

  *::-webkit-scrollbar {
    height: 2px;
    width: 2px;
    background-color: #ffffff;
  }

  *::-webkit-scrollbar-thumb {
    background-color: #222222;
    border: none;
  }

  *::-webkit-scrollbar-button {
    display: none;
  }
`;

export const SHARED_CLASSES = css`
  /* Semantic type classes — the Graphik scale (Figma node 16231:27015).
     Sizes flip mobile -> desktop via the tokens at the 600px breakpoint. */
  .type-display {
    font-size: var(--fs-display);
    line-height: var(--lh-display);
    letter-spacing: var(--ls-display);
    font-weight: var(--fw-regular);
  }

  .type-headline {
    font-size: var(--fs-headline);
    line-height: var(--lh-headline);
    letter-spacing: var(--ls-headline);
    font-weight: var(--fw-regular);
  }

  .type-title {
    font-size: var(--fs-title);
    line-height: var(--lh-title);
    letter-spacing: var(--ls-title);
    font-weight: var(--fw-regular);
  }

  .type-title-small {
    font-size: var(--fs-title-small);
    line-height: var(--lh-title-small);
    letter-spacing: var(--ls-title-small);
    font-weight: var(--fw-regular);
  }

  .type-label {
    font-size: var(--fs-label);
    line-height: var(--lh-label);
    letter-spacing: var(--ls-label);
    font-weight: var(--fw-medium);
  }

  .type-label-small {
    font-size: var(--fs-label-small);
    line-height: var(--lh-label-small);
    letter-spacing: var(--ls-label-small);
    font-weight: var(--fw-medium);
  }

  .type-body {
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    letter-spacing: var(--ls-body);
    font-weight: var(--fw-regular);
  }

  .type-body-small {
    font-size: var(--fs-body-small);
    line-height: var(--lh-body-small);
    letter-spacing: var(--ls-body-small);
    font-weight: var(--fw-regular);
  }

  .type-caption {
    font-size: var(--fs-caption);
    line-height: var(--lh-caption);
    letter-spacing: var(--ls-caption);
    font-weight: var(--fw-regular);
  }

  .type-cta {
    font-size: var(--fs-cta);
    line-height: var(--lh-cta);
    letter-spacing: var(--ls-cta);
    font-weight: var(--fw-medium);
  }

  .type-cta-link {
    font-size: var(--fs-cta-link);
    line-height: var(--lh-cta-link);
    letter-spacing: var(--ls-cta-link);
    font-weight: var(--fw-medium);
  }

  .type-barrel-title {
    font-size: var(--fs-label-small);
    line-height: var(--lh-label-small);
    letter-spacing: var(--ls-label-small);
    font-weight: var(--fw-medium);
    text-transform: uppercase;
  }

  .description-text {
    margin-top: 3.75rem;
    margin-bottom: 2.25rem;
  }

  .cta-plain-text {
    font-weight: 500;
    font-size: 0.875rem;
    letter-spacing: -0.21px;
    line-height: 1.25em;
    white-space: nowrap;
  }

  .font-size-small {
    font-size: calc(10rem / var(--default-rem-px-count));
  }

  .cta-plain-text > span {
    padding-right: 15px;
  }

  .block-wrapper {
    max-width: 1440px;
    margin: 0 auto;
  }

  .horizontal-rule {
    grid-column: 2 / 8;
    grid-row: 1 / last-line;
    border-bottom-style: solid;
    border-bottom-color: var(--block-rule-color, #c0c0c0);
    /* Spans every row, so it has to stay transparent to input. A negative
       z-index would drop it under the host background-color and never show. */
    pointer-events: none;
  }

  :host(.is-last-block) .horizontal-rule {
    display: none;
  }

  .cta-btn {
    width: fit-content;
    white-space: nowrap;
  }

  .bold {
    font-weight: 500;
  }

  .font-16 {
    font-size: calc(16rem / var(--default-rem-px-count));
  }

  .download-cursor {
    cursor: url('/static/icons/SVG/download-12x16.svg')
        var(--default-rem-px-count) var(--default-rem-px-count),
      pointer;
  }

  .not-mobile {
    display: none;
  }

  .mobile-only {
    display: initial;
  }

  vimeo-video {
    display: contents;
    position: absolute;
    width: 100%;
  }

  @media (min-width: 600px) {
    .horizontal-rule {
      grid-column: 2 / 14;
      grid-row: 1 / last-line;
    }

    .font-size-small {
      font-size: calc(14rem / var(--default-rem-px-count));
    }

    .not-mobile {
      display: initial;
    }

    .mobile-only {
      display: none;
    }

    vimeo-video {
      height: 100%;
    }
  }

  /*
    !important because if it isn't supposed to be hidden it shouldn't have this
    class
  */
  .hidden {
    display: none !important;
  }

  .print-only {
    display: none;
  }

  @media print {
    /*
      !important seems to be necessary as specificity cannot be changed and it
      is otherwise needed for expected results
      This also seems to be an acceptable case for !important
    */
    .print-none {
      display: none !important;
    }

    .print-hidden {
      visibility: hidden !important;
    }

    .print-only {
      display: initial;
    }
  }
`;
